<template>
  <div class="container-wrapper">
    <div class="grid-container-wrapper">
      <TextInput
        label="船員手帳番号"
        placeholder="例) 88888"
        :isRequired="false"
        v-model="form.identifier"
        :errorMessage="validationErrors.identifier"
      />
      <DateInput
        label="取得日（最終更新日）"
        placeholder="---- / -- / --"
        :disableFutureDates="true"
        class="grid-column-start"
        v-model="form.issueDate"
        :errorMessage="validationErrors.issueDate"
      />
      <DateInput
        label="次回更新期限"
        placeholder="---- / -- / --"
        v-model="form.nextUpdateDate"
        :errorMessage="validationErrors.nextUpdateDate"
      />
      <ImageInput
        :maxSizeMB="5"
        :isRequired="false"
        class="grid-full-width"
        v-model="form.attachments"
        @change="handleImageInputChange"
      />
      <p v-if="validationErrors.attachments" class="form-error grid-full-width">
        {{ validationErrors.attachments }}
      </p>
      <LongTextInput
        label="備考"
        placeholder="備考を入力してください"
        class="grid-full-width"
        v-model="form.remarks"
        :errorMessage="validationErrors.remarks"
      />
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import TextInput from "../../../components/form/TextInput.vue";
import DateInput from "../../../components/form/DateInput.vue";
import ImageInput from "../../../components/form/ImageInput.vue";
import LongTextInput from "../../../components/form/LongTextInput.vue";

export default {
  name: "SeamansBookForm",
  components: {
    TextInput,
    DateInput,
    ImageInput,
    LongTextInput,
  },
  computed: {
    ...mapGetters("crewSettingsLicense", ["getLicenseTermYearsById"]),
  },
  props: {
    license: {
      type: String,
      default: "",
    },
    licenseOptions: {
      type: Array,
      default: () => [],
    },
    initialFields: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      form: this.buildInitialFormState(),
      validationErrors: {},
      isHydrating: false,
      imageChangeData: null, // ImageInputからの変更データを保持
      currentLicenseTermYears: null, // 現在選択されている資格の有効期限
    };
  },

  watch: {
    /** ライセンスプロップが変更された場合、フォームタイプを更新 */
    license(newValue) {
      if (newValue) {
        this.form.licenseType = newValue;
        this.emitLicenseTypeChange();
        this.currentLicenseTermYears = this.getLicenseTermYearsById(newValue);
      }
    },

    /** ライセンスオプション変更時、デフォルト値を設定 */
    licenseOptions: {
      handler(newOptions) {
        if (!this.form.licenseType && newOptions.length > 0) {
          this.form.licenseType = newOptions[0].id;
        }
        this.emitLicenseTypeChange();
      },
      immediate: true,
    },

    /** 取得日が変更された場合、次回更新期限に自動入力 */
    "form.issueDate"(newValue) {
      if (this.isHydrating) {
        return;
      }
      this.currentLicenseTermYears = this.getLicenseTermYearsById(
        this.form.licenseType
      );
      if (newValue && this.currentLicenseTermYears) {
        this.calculateNextUpdateDate(newValue);
      }
    },

    /** 初期値変更時にフォームへ適用 */
    initialFields: {
      handler() {
        this.applyInitialFields();
      },
      deep: true,
    },
  },

  mounted() {
    this.emitLicenseTypeChange();
  },

  methods: {
    /** 初期値を考慮したフォームを生成 */
    buildInitialFormState() {
      return {
        ...this.createDefaultForm(),
        ...this.extractInitialFields(this.initialFields),
      };
    },

    /** 受け取った初期値から利用するフィールドのみ抽出 */
    extractInitialFields(fields = {}) {
      const allowed = [
        "licenseType",
        "identifier",
        "issueDate",
        "nextUpdateDate",
        "attachments",
        "remarks",
      ];
      return allowed.reduce((acc, key) => {
        if (fields[key] !== undefined && fields[key] !== null) {
          acc[key] = fields[key];
        }
        return acc;
      }, {});
    },

    /** 初期値をフォームへ反映 */
    applyInitialFields() {
      this.isHydrating = true;
      this.form = this.buildInitialFormState();
      this.validationErrors = {};
      this.emitLicenseTypeChange();
      this.$nextTick(() => {
        this.isHydrating = false;
      });
    },

    /** デフォルトのフォーム値を作成 */
    createDefaultForm() {
      return {
        licenseType: this.license || "",
        identifier: "",
        issueDate: "",
        nextUpdateDate: "",
        attachments: [],
        remarks: "",
      };
    },

    /** 親コンポーネントへライセンスタイプの変更を通知 */
    emitLicenseTypeChange() {
      const value = this.form.licenseType;
      const option = this.licenseOptions.find(
        option => option && option.id === value
      );
      this.$emit("license-type-change", option.label);
    },

    /** フォームをリセット */
    resetForm() {
      this.applyInitialFields();
    },

    /**
     * ImageInputからの変更を受け取る
     * @param {Object} changeData - {uploadedFiles, newFiles, removedFileIds}
     */
    handleImageInputChange(changeData) {
      this.imageChangeData = changeData;
    },

    /**
     * 取得日から有効期限を計算して次回更新期限に設定
     * @param {String} issueDate - 取得日（YYYY-MM-DD形式）
     */
    calculateNextUpdateDate(issueDate) {
      try {
        const date = new Date(issueDate);
        if (isNaN(date.getTime())) {
          return;
        }
        // 有効期限年数を追加
        date.setFullYear(date.getFullYear() + this.currentLicenseTermYears);
        // YYYY-MM-DD形式に変換
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, "0");
        const day = String(date.getDate()).padStart(2, "0");
        this.form.nextUpdateDate = `${year}-${month}-${day}`;
      } catch (error) {
        console.error("Error calculating next update date:", error);
      }
    },

    /** フォームを検証して妥当性を返す */
    validate() {
      this.validationErrors = {};

      if (this.form.identifier && this.form.identifier.length > 32) {
        this.$set(
          this.validationErrors,
          "identifier",
          "船員手帳番号は32文字以内で入力してください"
        );
      }

      // 取得日と次回更新期限の日付順序チェック
      if (this.form.nextUpdateDate && this.form.issueDate) {
        const issueDate = new Date(this.form.issueDate);
        const nextUpdateDate = new Date(this.form.nextUpdateDate);
        // 年月日だけに正規化（時分秒・ms を切り捨て）
        issueDate.setHours(0, 0, 0, 0);
        nextUpdateDate.setHours(0, 0, 0, 0);
        if (issueDate >= nextUpdateDate) {
          this.$set(
            this.validationErrors,
            "nextUpdateDate",
            "次回更新期限は取得日より後の日付を入力してください"
          );
        }
      }

      if (this.form.remarks && this.form.remarks.length > 255) {
        this.$set(
          this.validationErrors,
          "remarks",
          "備考は255文字以内で入力してください"
        );
      }

      return Object.keys(this.validationErrors).length === 0;
    },

    /** フォームデータをペイロード形式で返す */
    getPayload() {
      const files = [];
      const removeFileIds = this.imageChangeData
        ? this.imageChangeData.removedFileIds
        : [];

      // ImageInputから新規ファイルを取得
      if (this.imageChangeData && this.imageChangeData.newFiles) {
        this.imageChangeData.newFiles.forEach(fileObj => {
          if (fileObj.file instanceof File) {
            files.push(fileObj.file);
          }
        });
      }

      // APIペイロード用の基本情報
      const payload = {
        enterprise_crew_license_id: this.form.licenseType,
        number: this.form.identifier,
        expire_date: this.form.nextUpdateDate,
        start_date: this.form.issueDate,
        remarks: this.form.remarks || "",
        remove_enterprise_crew_license_file_id:
          removeFileIds.length > 0 ? removeFileIds : [],
      };

      // Fileオブジェクトをpayloadに含める
      payload.files = files;

      return payload;
    },
  },
};
</script>

<style lang="scss" scoped>
@import "@/assets/scss/aisea/_variables.scss";

.container-wrapper {
  max-width: 1200px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 24px;
  .grid-container-wrapper {
    width: 100%;
    display: grid;
    grid-template-columns: repeat(2, minmax(240px, 1fr));
    gap: 24px;
    flex-wrap: wrap;
    .grid-column-start {
      grid-column-start: 1;
    }
    .grid-full-width {
      grid-column: 1 / -1;
    }
    .form-error {
      color: $aisea-red;
      font-size: 12px;
      line-height: 1.5;
      margin-top: -4px;
      margin-bottom: 4px;
    }
  }
}
</style>
